import { Stack, Text } from '@mantine/core'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import useContentCurationAdmin from '@/hooks/useContentCurationAdmin'
import { ContentType } from '@/utils/curation'

interface FlagContentModalProps {
  close: () => void
  contentType: ContentType
  contentId: string
  isFlagged: boolean
}

const FlagContentModal: React.FC<FlagContentModalProps> = ({
  close,
  contentType,
  contentId,
  isFlagged,
}) => {
  const { t } = useTranslation()
  const { curate, isLoading, error } = useContentCurationAdmin()
  const [isReady, setIsReady] = useState(false)

  const handleFlag = useCallback(async () => {
    await curate(contentType, contentId, true)
    setIsReady(true)
  }, [contentType, curate, contentId])

  const handleUnflag = useCallback(async () => {
    await curate(contentType, contentId, false)
    setIsReady(true)
  }, [contentType, curate, contentId])

  const getContentTypeLabel = useCallback(() => {
    switch (contentType) {
      case 'creators':
        return t('content.types.creator', 'creator')
      case 'items':
        return t('content.types.item', 'item')
      case 'collections':
        return t('content.types.collection', 'collection')
      case 'collection_items':
        return t('content.types.collectionItem', 'collection and its items')
      default:
        return contentType
    }
  }, [contentType, t])

  return (
    <>
      {error.isError && (
        <ErrorMessage
          message={
            isFlagged
              ? t('components.modals.flagContent.errorUnflag', 'Error while unflagging content')
              : t('components.modals.flagContent.errorFlag', 'Error while flagging content')
          }
          details={error.msg}
        />
      )}
      {isLoading && (
        <ConfirmationLoading
          title={
            isFlagged
              ? t('components.modals.flagContent.unflagging', 'Unflagging Content...')
              : t('components.modals.flagContent.flagging', 'Flagging Content...')
          }
        />
      )}
      {isReady && !isLoading && !error.isError && (
        <ConfirmationReady
          message={
            isFlagged
              ? t('components.modals.flagContent.successUnflag', 'Content Unflagged Successfully')
              : t('components.modals.flagContent.successFlag', 'Content Flagged Successfully')
          }
          success={close}
        />
      )}
      {!isReady && !error.isError && !isLoading && (
        <>
          <Stack>
            {isFlagged ? (
              <Text>
                {t('components.modals.flagContent.unflag', {
                  defaultValue:
                    'Are you sure you want to unflag this {{type}}? This action will make it visible on the HUB.',
                  type: getContentTypeLabel(),
                })}
              </Text>
            ) : (
              <Text>
                {t('components.modals.flagContent.confirm', {
                  defaultValue:
                    'Are you sure you want to flag this {{type}}? This action will hide it from the HUB.',
                  type: getContentTypeLabel(),
                })}
              </Text>
            )}
          </Stack>
          <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
            <RootButton onClick={close} secondary style1={false} expand>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            {isFlagged ? (
              <RootButton onClick={handleUnflag} style1={false} expand>
                {t('buttons.unflag', 'Unflag')}
              </RootButton>
            ) : (
              <RootButton onClick={handleFlag} style1={false} expand>
                {t('buttons.flag', 'Flag')}
              </RootButton>
            )}
          </Stack>
        </>
      )}
    </>
  )
}

export default FlagContentModal
